// connectionWatchdog.js — keeps the Supabase connection alive and triggers
// incremental syncs so the local PGlite DB stays current.
//
// What it does every CHECK_INTERVAL_MS:
//   1. Ping Supabase with a lightweight query.
//   2. If ping fails: try refreshing the auth session, then retry once.
//   3. If ping succeeds and MIN_SYNC_INTERVAL_MS has elapsed: run syncAll().
//
// Additional triggers:
//   - Page gains focus (visibilitychange / window focus) → immediate ping + sync.
//   - Online event → immediate ping + sync.
//
// The watchdog never throws — all errors are logged and swallowed so it can
// never crash the app.

import { supabase } from './supabase'
import { getLocalDb, getPendingMutations, markMutationDone, markMutationFailed } from './localDb'
import { syncAll, getSyncSummary } from './syncEngine'

const CHECK_INTERVAL_MS    = 45_000  // health check every 45 s
const MIN_SYNC_INTERVAL_MS = 60_000  // don't re-sync more often than 60 s
const MIN_FOCUS_RECHECK_MS = 20_000  // skip focus-triggered ping if checked within 20 s
const OFFLINE_THRESHOLD    = 5       // consecutive failures before declaring offline (~3.75 min)

let _checkTimer       = null
let _lastSyncAt       = 0
let _lastCheckAt      = 0
let _consecutiveFails = 0
let _isSyncing        = false
let _isOnline         = navigator.onLine
let _syncListeners = []   // (summary) => void  — called after each sync
let _connListeners = []   // (isOnline: boolean) => void  — called on Supabase reachability change

// ─── Public API ───────────────────────────────────────────────────────────────

export function startWatchdog() {
  _scheduleCheck(0)   // immediate first check on startup

  // Page visibility / focus
  document.addEventListener('visibilitychange', _onVisibility)
  window.addEventListener('focus', _onFocus)

  // Network online/offline
  window.addEventListener('online',  _onOnline)
  window.addEventListener('offline', _onOffline)
}

export function stopWatchdog() {
  clearTimeout(_checkTimer)
  document.removeEventListener('visibilitychange', _onVisibility)
  window.removeEventListener('focus', _onFocus)
  window.removeEventListener('online',  _onOnline)
  window.removeEventListener('offline', _onOffline)
}

// Subscribe to sync completion events (e.g. to update a status badge in UI).
// Returns an unsubscribe function.
export function onSyncComplete(listener) {
  _syncListeners.push(listener)
  return () => { _syncListeners = _syncListeners.filter(l => l !== listener) }
}

// Subscribe to Supabase reachability changes.
// Listener receives (isOnline: boolean). Returns an unsubscribe function.
export function onConnectionChange(listener) {
  _connListeners.push(listener)
  return () => { _connListeners = _connListeners.filter(l => l !== listener) }
}

// Force an immediate sync regardless of timing — useful after a mutation.
export async function forceSync() {
  await _runSyncIfDue(true)
}

// Returns true if Supabase is reachable (based on last check).
export function isOnline() {
  return _isOnline
}

// ─── Internal ─────────────────────────────────────────────────────────────────

function _scheduleCheck(delayMs = CHECK_INTERVAL_MS) {
  clearTimeout(_checkTimer)
  _checkTimer = setTimeout(_check, delayMs)
}

async function _check() {
  _lastCheckAt = Date.now()
  try {
    const reachable = await _ping()

    if (!reachable) {
      _consecutiveFails++
    } else {
      _consecutiveFails = 0
      // Network is fine — but the supabase-js CLIENT can still be wedged on a
      // stuck token refresh (every query in the app hangs while /api/ping is
      // green). Probe the real client; if it's wedged, force a session refresh
      // and tell every view to refetch once it recovers.
      const clientOk = await _probeClient()
      if (!clientOk) {
        console.warn('[watchdog] supabase client wedged (network OK) — forcing session refresh')
        await _tryRefreshSession()
        if (await _probeClient()) {
          console.info('[watchdog] supabase client recovered — broadcasting refetch')
          window.dispatchEvent(new Event('chatrone:connection-recovered'))
        }
      }
    }

    // Require OFFLINE_THRESHOLD consecutive failures before flipping to offline —
    // prevents a single slow ping from flashing the banner unnecessarily.
    if (!reachable && _consecutiveFails >= OFFLINE_THRESHOLD && _isOnline) {
      _isOnline = false
      _connListeners.forEach(l => { try { l(false) } catch {} })
      console.warn('[watchdog] Supabase unreachable — attempting session refresh')
      await _tryRefreshSession()
      // Re-ping after refresh attempt
      _isOnline = await _ping()
      if (_isOnline) {
        _consecutiveFails = 0
        console.info('[watchdog] reconnected after session refresh')
        _connListeners.forEach(l => { try { l(true) } catch {} })
      }
    } else if (reachable && !_isOnline) {
      _isOnline = true
      _consecutiveFails = 0
      _connListeners.forEach(l => { try { l(true) } catch {} })
      console.info('[watchdog] Supabase reconnected')
    }

    if (_isOnline) {
      await _runSyncIfDue()
    }
  } catch (err) {
    console.warn('[watchdog] check error:', err?.message)
  } finally {
    _scheduleCheck()
  }
}

async function _ping() {
  // Hit a zero-DB Vercel edge endpoint — responds in <50ms with no DB involvement.
  // This correctly distinguishes "I'm truly offline" from "Supabase is slow under
  // cron load" — the latter should never show the offline banner.
  try {
    const res = await Promise.race([
      fetch('/api/ping', { cache: 'no-store' }),
      new Promise((_, reject) => setTimeout(() => reject(new Error('ping timeout')), 8_000)),
    ])
    return res.ok
  } catch {
    return false
  }
}

// Probe the supabase-js client itself with a cheap HEAD count. /api/ping only
// proves the network; this proves queries actually resolve. 8s race so a
// wedged client can't stall the watchdog loop.
async function _probeClient() {
  try {
    const probe = supabase
      .from('user_profiles')
      .select('id', { head: true, count: 'exact' })
      .limit(1)
    const res = await Promise.race([
      probe,
      new Promise((_, reject) => setTimeout(() => reject(new Error('client probe timeout')), 8_000)),
    ])
    return !res?.error
  } catch {
    return false
  }
}

async function _tryRefreshSession() {
  try {
    // Bounded — an unbounded hang here would kill the watchdog loop itself
    // (the finally/reschedule never runs).
    const { error } = await Promise.race([
      supabase.auth.refreshSession(),
      new Promise((_, reject) => setTimeout(() => reject(new Error('refreshSession timeout')), 15_000)),
    ])
    if (error) console.warn('[watchdog] session refresh error:', error.message)
  } catch (err) {
    console.warn('[watchdog] session refresh threw:', err?.message)
  }
}

async function _drainMutationQueue() {
  try {
    const pending = await getPendingMutations(20)
    if (!pending.length) return
    console.info(`[watchdog] draining ${pending.length} queued mutation(s)`)
    for (const mut of pending) {
      try {
        const payload = mut.payload
          ? (typeof mut.payload === 'string' ? JSON.parse(mut.payload) : mut.payload)
          : {}
        if (mut.operation === 'delete') {
          const { error } = await supabase.from(mut.table_name).delete().eq('id', mut.row_id)
          if (error) throw error
        } else if (mut.operation === 'update') {
          // Partial update — strip id from body (PostgREST 400s when PK is in PATCH body)
          const { id: _dropId, ...updateFields } = payload
          const { error } = await supabase.from(mut.table_name).update(updateFields).eq('id', mut.row_id)
          if (error) throw error
        } else {
          // insert / upsert — full row, safe to upsert (client generated UUID makes it idempotent)
          const { error } = await supabase.from(mut.table_name).upsert(payload)
          if (error) throw error
        }
        await markMutationDone(mut.id)
      } catch (err) {
        await markMutationFailed(mut.id, err?.message)
        console.warn(`[watchdog] mutation ${mut.id} (${mut.table_name} ${mut.operation}) failed:`, err?.message)
      }
    }
  } catch (err) {
    console.warn('[watchdog] mutation queue drain error:', err?.message)
  }
}

async function _runSyncIfDue(force = false) {
  if (_isSyncing) return
  if (!getLocalDb()) return   // PGlite not ready yet

  const elapsed = Date.now() - _lastSyncAt
  if (!force && elapsed < MIN_SYNC_INTERVAL_MS) return

  _isSyncing = true
  try {
    await _drainMutationQueue()
    const results = await syncAll()
    _lastSyncAt = Date.now()
    const summary = await getSyncSummary()
    _syncListeners.forEach(l => { try { l(summary, results) } catch {} })
  } catch (err) {
    console.warn('[watchdog] sync error:', err?.message)
  } finally {
    _isSyncing = false
  }
}

// ─── Event handlers ───────────────────────────────────────────────────────────

function _onVisibility() {
  if (!document.hidden) _onFocus()
}

function _onFocus() {
  // Re-check when the tab comes into view — but skip if we just checked recently
  // to avoid flashing the offline banner on every tab switch.
  if (Date.now() - _lastCheckAt < MIN_FOCUS_RECHECK_MS) return
  _scheduleCheck(0)
}

function _onOnline() {
  console.info('[watchdog] browser reports online')
  _scheduleCheck(0)
}

function _onOffline() {
  console.info('[watchdog] browser reports offline')
  _isOnline = false
}
